import traverse from 'traverse';

import {emptyObject} from '@packages/utilities/fp/utils';
import {tokens} from '@packages/utilities/styled-tokens';

import {pseudoSelectors as pseudos} from './pseudos';
import {systemConfig, isStyleProp} from './config';
import {objectFilter} from './utils';

/**
 * Replaces all instances of a given substring (prefaced with `$`) with a theme token defined by `config`.
 *
 * Returns a string if the config is a simple mapping.
 * Returns an object of mapped tokens if the config provides a config function.
 */
export const convertToThemeToken = (property, config, value) => {
  const {scale, transform, properties} = config;

  let themeConfig;

  /**
   * Logic for dealing with typography tokens, since they need to be restructured to match our theme object shape
   */
  if (scale === 'typography') {
    const fontType = value.replace('$', '');
    themeConfig = tokens[scale]?.[property]?.[fontType];
    if (!themeConfig) {
      return value;
    } else {
      if (typeof transform === 'function') {
        return transform(themeConfig);
      } else {
        return themeConfig;
      }
    }
  } else {
    themeConfig = tokens[scale];
  }

  /**
   * Returns value if it's not supported in the system.
   */
  if (!themeConfig) return value;

  // Some properties are transformed system-wide, converting our tokens into CSS values.
  const systemTransformer = systemConfig.transformers[scale];

  /**
   * Walk through the value and replace all instances of '$' substrings with appropriate tokens
   */
  const replacementFunction = (val) => {
    const rawValue = val.replace('$', '');
    return systemTransformer
      ? systemTransformer(themeConfig[rawValue])
      : themeConfig[rawValue];
  };

  /**
   * This regex is searching for our tokens in the form of `$stringOrNumericToken`, `$-numericToken`, `$numeric.Token`
   * I tried using Object.keys(space) to construct the digit replacement part of our regex.
   * Unfortunately, because we have decimal places along with both positive and negative numbers, our regex was matching incorrectly.
   * Since Regex matches in order, we can't simply match [-1, -0.5, -0.25, 0, 0.25, 0.5, 1], since 0.25 and 0.5 would never match (getting got by the 0).
   *
   * So, here we are. A bit of a mess, but it works.
   */
  let tokenizedValue = value.replaceAll(
    /(\$\b[a-zA-Z]+\d*|\$-?\b\d+(?:\.?\d{1,2})?\b)/g,
    replacementFunction
  );

  /**
   * We are checking the value's length because we only want to process it through the config function if it _only_ contains a single $token.
   * Otherwise, we want to return the original string with the $tokens parsed in place.
   *
   * For example:
   *  `border: '2px dotted $border1'` should return `border: '2px dotted var(--colors-border-1)'`
   *  `border: '$border1'` should use the config function to return `border: '1px solid var(--colors-border-1)'`
   */

  if (transform) {
    const valueArr = tokenizedValue.split(' ');

    if (valueArr.length === 1 || valueArr.length === 2) {
      tokenizedValue = valueArr
        .map((val, index) => {
          if (index === 0) {
            return transform(val);
          } else if (index === 1 && val === '!important') {
            return val;
          }
        })
        .join(' ');
    }
  }

  /**
   * Some of our scale values are shortcuts for standard CSS properties.
   * We need to expand them into a valid style object (e.g. `paddingX => {paddingLeft: value, paddingRight: value)`)
   */
  if (Array.isArray(properties)) {
    const expandedValues = properties.reduce((acc, cssProperty) => {
      acc[cssProperty] = tokenizedValue;
      return acc;
    }, {});

    return expandedValues;
  }

  return tokenizedValue;
};

/**
 * Converts our set of pseudo-props into pseudo-selectors.
 */
export const convertPseudos = (styles = {}) => {
  if (!Object.keys(styles).length) return styles;
  const {
    _active,
    _disabled,
    _focus,
    _focusVisible,
    _focusWithin,
    _hover,
    _selected,
    ...otherStyles
  } = styles;

  const computedStyles = {};
  /**
   * Pseudo styles must be applied in a specific order.
   */
  const orderedDefinedPseudoStyles = objectFilter(
    {
      _hover,
      _active,
      _focus,
      _focusWithin,
      _focusVisible,
      _selected,
      _disabled,
    },
    Boolean
  );

  for (const pseudo in orderedDefinedPseudoStyles) {
    const key = pseudos[pseudo];
    const value = orderedDefinedPseudoStyles[pseudo];
    computedStyles[key] = value;
  }

  for (let key in otherStyles) {
    const value = styles[key];
    const isPseudo = key in pseudos;

    /**
     * converts pseudo shorthands to valid selector
     * "_expanded" => "&[aria-expanded=true], &[data-expanded]"
     */
    if (isPseudo) {
      key = pseudos[key];
    }
    computedStyles[key] = value;
  }

  return computedStyles;
};

/**
 * Traverses a style object, searching for substrings marked by `$`
 * If it finds any, it will ship them off to `convertToThemeToken` for processing.
 */
export const remapStyleTokens = (styles = emptyObject) => {
  const collectedStylesForRoot = {};

  const newStyles = traverse(styles).map(function (value) {
    if (this.isLeaf && typeof value === 'string') {
      const scale = systemConfig[this.key];

      if (scale && value.includes('$')) {
        const property = this.path.slice(-1)[0]; // the css property being set (e.g. 'paddingX')
        const convertedTokens = convertToThemeToken(property, scale, value);

        if (typeof convertedTokens === 'object') {
          /**
           * Some config options return an object (e.g. paddingX returns { paddingLeft, paddingRight }).
           * These options need to be incorporated into the style at the same they were defined.
           *
           * If we are at the root of our object, we collect these converted tokens and spread them out later
           *
           * If we are nested (e.g. within an `&:hover` key), we need to update the parent with the newly converted tokens.
           */
          if (this.parent.isRoot) {
            Object.assign(collectedStylesForRoot, convertedTokens);
          } else {
            /**
             * Be aware that this gives converted tokens a higher "priority" than other keys in the parent declaration.
             * This is REGARDLESS of their original order in the style itself.
             *
             * This only happens in a given declaration block.
             * It does not affect inheritance or composition in any way (those are handled by passing className)
             *
             * ```
             * const styles = {
             *   paddingX: '$8',
             *   paddingLeft: '0px'
             * }
             * ```
             *
             * will output:
             * ```
             * const styles = {
             *   paddingLeft: '24px',
             *   paddingRight: '24px'
             * }
             * ```
             */
            this.parent.update({
              ...this.parent.node,
              ...convertedTokens,
            });
          }
          /**
           * We want to delete our shorthand keys from the style object (e.g. `paddingX`)
           */
          this.delete();
        } else {
          this.update(convertedTokens);
        }
      }
    }
  });

  return {
    ...newStyles,
    ...collectedStylesForRoot,
  };
};

/**
 * Checks incoming props for style system props, converts any pseudos, and returns a style object for processing.
 */
export const getSystemStyles = (props) => {
  const stylesPropsForSystem = objectFilter(props, (_, prop) =>
    isStyleProp(prop)
  );

  if (!stylesPropsForSystem) return;

  const newStyles = convertPseudos(stylesPropsForSystem);
  return newStyles;
};
